import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface DashboardLayoutProps {
  children: ReactNode;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigation = [
    { name: '대시보드', href: '/dashboard', icon: '🏠' },
    { name: '규칙 관리', href: '/rules', icon: '📋' },
    { name: '필터 관리', href: '/filters', icon: '🔍' },
    { name: '제재 관리', href: '/punishments', icon: '⚖️' },
    { name: '제재 기록', href: '/history', icon: '📜' },
    { name: '화이트리스트', href: '/whitelist', icon: '✅' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Skip to main content link for keyboard navigation */}
      <a href="#main-content" className="skip-to-main">
        메인 콘텐츠로 건너뛰기
      </a>

      {/* 상단 네비게이션 */}
      <nav className="bg-white shadow-sm border-b border-gray-200" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-[#FF7A7A] to-[#DD8DFF] bg-clip-text text-transparent">
                Foveo Dashboard
              </h1>
            </div>

            {/* 데스크톱 사용자 메뉴 */}
            <div className="hidden md:flex items-center space-x-4">
              <span className="text-sm text-gray-700" aria-label="Current user">
                {user?.username}#{user?.discriminator}
              </span>
              <button
                onClick={logout}
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded px-3 py-1"
                aria-label="Logout"
              >
                로그아웃
              </button>
            </div>

            {/* 모바일 메뉴 버튼 */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-600 hover:text-gray-900 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* 모바일 메뉴 */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200" id="mobile-menu">
            <div className="px-2 pt-2 pb-3 space-y-1" role="menu">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-3 py-2 rounded-md text-base font-medium focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${
                    isActive(item.href)
                      ? 'bg-gradient-to-r from-[#FF7A7A] to-[#DD8DFF] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  role="menuitem"
                  aria-current={isActive(item.href) ? 'page' : undefined}
                >
                  <span aria-hidden="true">{item.icon}</span> {item.name}
                </Link>
              ))}
              <button
                onClick={logout}
                className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                role="menuitem"
              >
                로그아웃
              </button>
            </div>
          </div>
        )}
      </nav>

      <div className="flex">
        {/* 사이드바 (데스크톱) */}
        <aside className="hidden md:flex md:flex-shrink-0" aria-label="Sidebar navigation">
          <div className="flex flex-col w-64">
            <div className="flex flex-col flex-grow bg-white border-r border-gray-200 pt-5 pb-4 overflow-y-auto">
              <nav className="mt-5 flex-1 px-2 space-y-1" role="navigation">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${
                      isActive(item.href)
                        ? 'bg-gradient-to-r from-[#FF7A7A] to-[#DD8DFF] text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    aria-current={isActive(item.href) ? 'page' : undefined}
                  >
                    <span className="mr-3 text-lg" aria-hidden="true">{item.icon}</span>
                    {item.name}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        </aside>

        {/* 메인 콘텐츠 */}
        <main id="main-content" className="flex-1 overflow-y-auto" role="main" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}
